import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { getBillingStatus, listBillingPlans } from "./api";
import { useAuth } from "./auth-context";
import type { BillingPlan, BillingStatus, PlanFeatureAccess } from "../types/domain";

interface SubscriptionContextValue {
  status: BillingStatus | null;
  plans: BillingPlan[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { tokens, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadBillingState() {
    if (!tokens?.access_token) {
      setStatus(null);
      setPlans([]);
      setError(null);
      setFetching(false);
      return;
    }

    setFetching(true);
    try {
      const [billingStatus, billingPlans] = await Promise.all([
        getBillingStatus(tokens.access_token),
        listBillingPlans(tokens.access_token)
      ]);
      setStatus(billingStatus);
      setPlans(billingPlans);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore caricamento billing");
    } finally {
      setFetching(false);
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus(null);
      setPlans([]);
      setError(null);
      setFetching(false);
      return;
    }
    void loadBillingState();
  }, [isAuthenticated, tokens?.access_token]);

  // True while fetching OR when authenticated but status not yet resolved.
  // This prevents a flash where isAuthenticated=true but status is still null
  // (the brief window between saveSession() and the billing useEffect running).
  const loading = fetching || (isAuthenticated && status === null && !error);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      status,
      plans,
      loading,
      error,
      refresh: loadBillingState
    }),
    [status, plans, fetching, error, isAuthenticated]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription deve essere usato dentro SubscriptionProvider");
  }
  return ctx;
}

export function useFeature(featureKey: string): {
  feature: PlanFeatureAccess | null;
  enabled: boolean;
  limit: number | null;
  usage: number | null;
  remaining: number | null;
  atLimit: boolean;
} {
  const { status } = useSubscription();
  const feature = status?.feature_access.find((item) => item.feature_key === featureKey) ?? null;
  const limit = feature?.limit_value ?? null;
  const usage = feature?.current_usage ?? null;
  const remaining = limit !== null && usage !== null ? Math.max(limit - usage, 0) : null;

  return {
    feature,
    enabled: Boolean(feature?.enabled),
    limit,
    usage,
    remaining,
    atLimit: limit !== null && usage !== null ? usage >= limit : false
  };
}

export function useFeatureLimit(featureKey: string): number | null {
  return useFeature(featureKey).limit;
}

export function FeatureGuard({
  featureKey,
  fallback = null,
  children
}: {
  featureKey: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { enabled } = useFeature(featureKey);
  return enabled ? <>{children}</> : <>{fallback}</>;
}
